import React from 'react';

/**
 * PlatformMark - the one place a platform's visual identity is defined.
 *
 * Platforms were previously rendered as a grey `<Badge>{account.platform}</Badge>`,
 * so every network looked identical and a list of accounts gave no scannable
 * signal about which one you were looking at. Recognising a network by its mark
 * is faster than reading its name, which is why every comparable product leads
 * with the logo.
 *
 * Kept as a UI primitive with NO business logic: which platforms are *enabled*
 * is owned by ScanService.getSupportedPlatforms(). This module owns only how a
 * platform LOOKS, so the two can't drift into disagreeing about either question.
 *
 * Accepts the display casing stored on Account.platform ("TikTok", "X") and the
 * lowercase form on Scan.platform ("tiktok", "x") - both normalise to one key.
 */

const MARKS = {
	x: {
		label: 'X',
		fullLabel: 'X (Twitter)',
		// Brand surfaces, used for the icon chip background + ring.
		className: 'bg-ink text-white',
		softClassName: 'bg-muted text-ink',
		path: 'M13.6 10.6 21 2h-1.8l-6.4 7.5L7.7 2H2l7.8 11.3L2 22h1.8l6.8-7.9 5.4 7.9H22l-8.4-11.4Zm-2.4 2.8-.8-1.1L4.4 3.3h2.7l5.1 7.3.8 1.1 6.6 9.4h-2.7l-5.7-8.1Z',
	},
	tiktok: {
		label: 'TikTok',
		fullLabel: 'TikTok',
		className: 'bg-[#010101] text-white',
		softClassName: 'bg-muted text-[#010101]',
		path: 'M16.5 2h-2.9v12.4a2.6 2.6 0 1 1-2.6-2.6c.2 0 .4 0 .6.1V9a5.6 5.6 0 1 0 4.9 5.5V8.2a6.6 6.6 0 0 0 3.9 1.3V6.6a3.7 3.7 0 0 1-3.9-3.7V2Z',
	},
	reddit: {
		label: 'Reddit',
		fullLabel: 'Reddit',
		className: 'bg-[#FF4500] text-white',
		softClassName: 'bg-orange-50 text-[#FF4500] dark:bg-orange-500/10',
		path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm5.2 11.2a1.4 1.4 0 0 1-.1.5c.1.3.1.6.1.9 0 2.3-2.3 4.1-5.2 4.1s-5.2-1.8-5.2-4.1c0-.3 0-.6.1-.9a1.4 1.4 0 1 1 1.6-2.2 6.9 6.9 0 0 1 3.5-1.1l.7-3.2a.4.4 0 0 1 .5-.3l2.3.5a1 1 0 1 1-.1.8l-1.9-.4-.6 2.6a6.9 6.9 0 0 1 3.4 1.1 1.4 1.4 0 0 1 2.3 1.1c0 .2 0 .4-.1.6ZM9.3 13.8a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm5.7 3.1a3.9 3.9 0 0 1-2.9.9 3.9 3.9 0 0 1-3-.9.3.3 0 0 1 .4-.4c.6.5 1.5.7 2.6.7s2-.2 2.5-.7a.3.3 0 1 1 .4.4Zm-.3-2.1a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z',
	},
};

/** Anything unrecognised still renders, rather than collapsing the layout. */
const FALLBACK = {
	label: 'Platform',
	fullLabel: 'Platform',
	className: 'bg-accent text-foreground',
	softClassName: 'bg-muted text-muted-foreground',
	path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm2 13h-4v-1.5h1V11h-1V9.5h3v7h1V18Z',
};

export function getPlatformMark(platform) {
	return MARKS[String(platform || '').toLowerCase()] || FALLBACK;
}

const SIZES = {
	xs: { box: 'w-5 h-5 rounded-md', icon: 'w-3 h-3' },
	sm: { box: 'w-6 h-6 rounded-md', icon: 'w-3.5 h-3.5' },
	md: { box: 'w-8 h-8 rounded-lg', icon: 'w-4 h-4' },
	lg: { box: 'w-10 h-10 rounded-xl', icon: 'w-5 h-5' },
};

/** The mark on its brand-coloured chip. */
export function PlatformMark({ platform, size = 'md', soft = false, className = '' }) {
	const mark = getPlatformMark(platform);
	const s = SIZES[size] || SIZES.md;

	return (
		<span
			className={`inline-flex items-center justify-center shrink-0 ${s.box} ${
				soft ? mark.softClassName : mark.className
			} ${className}`}
			title={mark.fullLabel}
		>
			<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={s.icon}>
				<path d={mark.path} />
			</svg>
			<span className="sr-only">{mark.fullLabel}</span>
		</span>
	);
}

/** Mark plus name, for use inline in dense lists. */
export function PlatformTag({ platform, size = 'xs', className = '' }) {
	const mark = getPlatformMark(platform);
	return (
		<span className={`inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground ${className}`}>
			<PlatformMark platform={platform} size={size} />
			{mark.label}
		</span>
	);
}

export default PlatformMark;
